import { AdminLayout } from "./AdminLayout";
import { Plus, Search, MoreVertical, Calendar } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', status: 'planning', company_id: '', budget: ''
  });

  const fetchData = async () => {
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const [projectsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/projects`, { params, withCredentials: true }),
        axios.get(`${API}/crm/companies`, { withCredentials: true })
      ]);
      setProjects(projectsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [statusFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, budget: formData.budget ? parseFloat(formData.budget) : null };
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.project_id}`, data, { withCredentials: true });
        toast.success("Project updated");
      } else {
        await axios.post(`${API}/projects`, data, { withCredentials: true });
        toast.success("Project created");
      }
      setIsDialogOpen(false);
      setEditingProject(null);
      setFormData({ name: '', description: '', status: 'planning', company_id: '', budget: '' });
      fetchData();
    } catch (error) {
      toast.error("Failed to save project");
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name, description: project.description || '', status: project.status,
      company_id: project.company_id || '', budget: project.budget?.toString() || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`, { withCredentials: true });
      toast.success("Project deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const updateStatus = async (projectId, status) => {
    try {
      await axios.put(`${API}/projects/${projectId}`, { status }, { withCredentials: true });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getCompanyName = (companyId) => companies.find(c => c.company_id === companyId)?.name || '';

  const statuses = ['planning', 'active', 'on_hold', 'completed', 'cancelled'];
  const statusLabels = { planning: 'Planning', active: 'Active', on_hold: 'On Hold', completed: 'Completed', cancelled: 'Cancelled' };
  const statusColors = { planning: 'bg-blue-500/20 text-blue-400', active: 'bg-green-500/20 text-green-400', on_hold: 'bg-yellow-500/20 text-yellow-400', completed: 'bg-purple-500/20 text-purple-400', cancelled: 'bg-red-500/20 text-red-400' };

  return (
    <AdminLayout title="Delivery">
      <div className="space-y-6" data-testid="admin-projects">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">Active Deliveries</h2>
            <p className="text-sm text-[#8A8A8A]">{projects.length} total</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button className="btn-primary text-sm flex items-center gap-2"
                onClick={() => { setEditingProject(null); setFormData({ name: '', description: '', status: 'planning', company_id: '', budget: '' }); }}
                data-testid="add-project-btn">
                <Plus className="w-4 h-4" /> Add Project
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E]">
              <DialogHeader><DialogTitle>{editingProject ? 'Edit Delivery' : 'Add Delivery'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <input type="text" placeholder="Delivery Name *" required value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})} className="input-field" />
                <textarea placeholder="Description" rows={3} value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})} className="input-field resize-none" />
                <div className="grid grid-cols-2 gap-4">
                  <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="input-field">
                    {statuses.map(status => <option key={status} value={status}>{statusLabels[status]}</option>)}
                  </select>
                  <input type="number" placeholder="Budget (£)" value={formData.budget} min="0" step="0.01"
                    onChange={(e) => setFormData({...formData, budget: e.target.value})} className="input-field" />
                </div>
                <select value={formData.company_id} onChange={(e) => setFormData({...formData, company_id: e.target.value})} className="input-field">
                  <option value="">Select Client Company</option>
                  {companies.map(company => <option key={company.company_id} value={company.company_id}>{company.name}</option>)}
                </select>
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setIsDialogOpen(false)} className="btn-secondary text-sm">Cancel</button>
                  <button type="submit" className="btn-primary text-sm">{editingProject ? 'Update' : 'Create'}</button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-field w-48">
            <option value="">All Statuses</option>
            {statuses.map(status => <option key={status} value={status}>{statusLabels[status]}</option>)}
          </select>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className="card-dashboard animate-pulse">
                <div className="h-6 bg-[#2E2E2E] rounded w-3/4 mb-4" />
                <div className="h-4 bg-[#2E2E2E] rounded w-full mb-2" />
                <div className="h-4 bg-[#2E2E2E] rounded w-1/2" />
              </div>
            ))
          ) : projects.length === 0 ? (
            <div className="col-span-full text-center py-12 text-[#8A8A8A]">No active deliveries</div>
          ) : (
            projects.map((project) => (
              <div key={project.project_id} className="card-dashboard" data-testid={`project-card-${project.project_id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-bold mb-1">{project.name}</h3>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className={`badge ${statusColors[project.status]} cursor-pointer`}>{statusLabels[project.status]}</button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                        {statuses.map(status => (
                          <DropdownMenuItem key={status} onClick={() => updateStatus(project.project_id, status)}
                            className="cursor-pointer hover:bg-[#2E2E2E]">{statusLabels[status]}</DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1 hover:bg-[#2E2E2E]"><MoreVertical className="w-4 h-4" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                      <DropdownMenuItem onClick={() => handleEdit(project)} className="cursor-pointer hover:bg-[#2E2E2E]">Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(project.project_id)} className="cursor-pointer hover:bg-[#2E2E2E] text-red-400">Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {project.description && <p className="text-sm text-[#8A8A8A] mb-4 line-clamp-2">{project.description}</p>}
                <div className="flex justify-between text-sm">
                  {project.company_id && <span className="text-[#8A8A8A]">{getCompanyName(project.company_id)}</span>}
                  {project.budget && <span className="text-[#5F8FB8] font-bold">£{project.budget.toLocaleString()}</span>}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default Projects;
